import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import filemakerTestService from '../services/filemakerTestService';

const FileMakerTest = () => {
  const [testResults, setTestResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const runTests = async () => {
    setIsLoading(true);
    try {
      const results = await filemakerTestService.runFullTest();
      setTestResults(results);
    } catch (error) {
      console.error('Error ejecutando pruebas:', error);
      setTestResults({
        connection: {
          success: false,
          message: 'Error ejecutando pruebas: ' + error.message
        },
        layouts: {}
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (success) => {
    return (
      <Badge variant={success ? 'default' : 'destructive'}>
        {success ? '✅ Exitoso' : '❌ Error'}
      </Badge>
    );
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Pruebas de Conectividad FileMaker</CardTitle>
          <p className="text-sm text-gray-600">
            Herramienta de diagnóstico para verificar la conexión con FileMaker Server
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Button 
              onClick={runTests} 
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? 'Ejecutando pruebas...' : 'Ejecutar Pruebas de Conectividad'}
            </Button>

            {testResults && (
              <div className="space-y-4">
                {/* Resultado de conexión */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center justify-between">
                      Conexión Básica
                      {getStatusBadge(testResults.connection.success)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p><strong>Mensaje:</strong> {testResults.connection.message}</p>
                      {testResults.connection.error && (
                        <p><strong>Código de Error:</strong> {testResults.connection.error}</p>
                      )}
                      {testResults.connection.layouts && (
                        <p><strong>Layouts Disponibles:</strong> {testResults.connection.layouts.length}</p>
                      )}
                      {testResults.connection.details && (
                        <div>
                          <strong>Detalles:</strong>
                          <pre className="bg-gray-100 p-2 rounded text-xs mt-1 overflow-auto">
                            {JSON.stringify(testResults.connection.details, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Resultados de layouts */}
                {Object.keys(testResults.layouts).length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Acceso a Layouts</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {Object.entries(testResults.layouts).map(([layoutName, result]) => (
                          <div key={layoutName} className="border rounded p-3">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-medium">{layoutName}</h4>
                              {getStatusBadge(result.success)}
                            </div>
                            <div className="text-sm space-y-1">
                              <p><strong>Mensaje:</strong> {result.message}</p>
                              {result.recordCount !== undefined && (
                                <p><strong>Registros Encontrados:</strong> {result.recordCount}</p>
                              )}
                              {result.error && (
                                <p><strong>Código de Error:</strong> {result.error}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Información de configuración */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Configuración Actual</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <p><strong>URL API:</strong> {process.env.REACT_APP_FILEMAKER_API_URL || 'No configurada'}</p>
                      <p><strong>Token configurado:</strong> {process.env.REACT_APP_FILEMAKER_API_TOKEN ? 'Sí' : 'No'}</p>
                      <p><strong>Layout Familias:</strong> {process.env.REACT_APP_FILEMAKER_LAYOUT_FAMILIAS || 'No configurado'}</p>
                      <p><strong>Layout Planes:</strong> {process.env.REACT_APP_FILEMAKER_LAYOUT_PLANES || 'No configurado'}</p>
                      <p><strong>Layout URLs:</strong> {process.env.REACT_APP_FILEMAKER_LAYOUT_URLS || 'No configurado'}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FileMakerTest;